// ui/ListPage
import Card from "@mui/material/Card";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";

import SoftBox from "components/SoftBox";
import SoftInput from "components/SoftInput";
import SoftButton from "components/SoftButton";
import Table from "examples/Tables/Table";

import Divider from "@mui/material/Divider";
import TablePagination from "@mui/material/TablePagination";

import { ui } from "./tokens";

/**
 * ListPage
 * - Encabezado con título, buscador y botón “Agregar”
 * - Área de contenido opcional (children) arriba de la tabla
 * - Tabla (columns, rows) con estilos consistentes
 * - Estados: loading/error/empty
 */
export default function ListPage({
  title,
  search,
  onSearchChange,
  searchPlaceholder = "Buscar…",
  addText = "Agregar",
  onAdd,
  toolbar,            // node opcional: si lo pasas, reemplaza search + add
  columns = [],
  rows = [],
  loading = false,
  error = "",
  children,           // contenido extra arriba de la tabla
  cardSx,
  paginationProps,

  // 🔽 NUEVO: props de ordenamiento que vienen del padre (Sellos)
  sortField,          // nombre de la columna (por ejemplo "sello" o "creado")
  sortDirection,      // "asc" | "desc"
  onSortChange,       // función que el Table debe llamar al hacer click en encabezado
}) {
  const renderToolbar = () =>
    toolbar || (
      <SoftBox display="flex" alignItems="center" sx={{ gap: 2 }}>
        <SoftInput
          placeholder={loading ? "Cargando…" : searchPlaceholder}
          value={search}
          onChange={(e) => onSearchChange?.(e.target.value)}
          icon={{ component: "search", direction: "left" }}
          sx={{ width: 240 }}
          disabled={loading || !!error}
        />
        {onAdd && (
          <SoftButton variant="outlined" color="dark" onClick={onAdd}>
            {addText} &nbsp;+
          </SoftButton>
        )}
      </SoftBox>
    );

  return (
    <SoftBox py={3} sx={{ width: "100%", overflowX: "hidden" }}>
      <SoftBox mb={3}>
        <Card sx={{ ...ui.card, ...(cardSx || {}), overflow: "hidden", width: "100%" }}>
          {/* Header */}
          <SoftBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            p={3}
            pt={2}
            pb={2}
            sx={{ gap: 2 }}
          >
            <Typography variant="h6" fontWeight={800}>
              {title}
            </Typography>
            {renderToolbar()}
          </SoftBox>

          {/* Mensaje de error */}
          {error && (
            <SoftBox px={3} pb={1}>
              <Alert severity="error">{error}</Alert>
            </SoftBox>
          )}

          {/* Slot opcional arriba de la tabla (chips/filtros/etc.) */}
          {children && <SoftBox px={3} pb={2}>{children}</SoftBox>}

          {/* Tabla / estados */}
          <Box sx={{ width: "100%", overflowX: "auto" }}>
            <SoftBox
              sx={(theme) => ({
                "& .MuiTableRow-root:not(:last-child) td": `${
                  theme?.borders?.borderWidth?.[1] ?? "1px"
                } solid ${theme?.borders?.borderColor ?? "#eee"}`,
                "tbody tr:hover td": {
                  backgroundColor: "rgba(19, 75, 112, 0.04)",
                },
                "tbody tr": {
                  transition: "background-color .15s ease",
                },
              })}
            >
              {loading ? (
                <SoftBox display="flex" justifyContent="center" py={6}>
                  <CircularProgress />
                </SoftBox>
              ) : rows?.length ? (
                <Table
                  columns={columns}
                  rows={rows}
                  // 🔽 PASAMOS LA INFO DE ORDENAMIENTO A LA TABLA
                  sortField={sortField}
                  sortDirection={sortDirection}
                  onSortChange={onSortChange}
                  // si tu Table usa otros nombres (order, orderBy, onRequestSort):
                  // order={sortDirection}
                  // orderBy={sortField}
                  // onRequestSort={(columnName) => onSortChange?.(columnName)}
                />
              ) : (
                !error && (
                  <SoftBox px={3} py={5} textAlign="center" color="text.secondary">
                    No hay datos para mostrar.
                  </SoftBox>
                )
              )}
            </SoftBox>
          </Box>

          {/* Paginación (opcional) */}
          {paginationProps && (
            <>
              <Divider />
              <Box
                sx={{
                  px: 2,
                  py: 1,
                  width: "100%",
                  overflowX: "hidden",
                  display: "flex",
                  justifyContent: "flex-end",
                }}
              >
                <TablePagination
                  component="div"
                  {...paginationProps}
                  labelRowsPerPage=""
                  SelectProps={{
                    native: false,
                    variant: "outlined",
                    MenuProps: { disableScrollLock: true },
                  }}
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    "& .MuiTablePagination-toolbar": {
                      px: 0,
                      gap: 1,
                      minHeight: 48,
                      flexWrap: "nowrap",
                    },
                    "& .MuiTablePagination-spacer": { display: "none" },
                    "& .MuiTablePagination-selectLabel": {
                      m: 0,
                      whiteSpace: "nowrap",
                    },
                    "& .MuiTablePagination-displayedRows": {
                      m: 0,
                      whiteSpace: "nowrap",
                    },
                    "& .MuiTablePagination-input": {
                      m: 0,
                      width: "auto !important",
                      maxWidth: "unset !important",
                      flex: "0 0 auto !important",
                      display: "inline-flex !important",
                    },
                    "& .MuiTablePagination-input .MuiFormControl-root": {
                      width: "auto !important",
                      maxWidth: "unset !important",
                      flex: "0 0 auto !important",
                    },
                    "& .MuiTablePagination-input .MuiInputBase-root": {
                      width: "auto !important",
                      minWidth: 64,
                      maxWidth: 90,
                      flex: "0 0 auto !important",
                    },
                    "& .MuiTablePagination-input .MuiSelect-select": {
                      minWidth: "auto",
                      padding: "6px 24px 6px 8px",
                    },
                    "& .MuiTablePagination-actions": { ml: 1 },
                  }}
                />
              </Box>
            </>
          )}
        </Card>
      </SoftBox>
    </SoftBox>
  );
}
